import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "@/components/ui/tabs";
import { CheckCircle2, XCircle, Clock, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const RISK_STYLES = {
  low: { color: "#10B981", label: "Bajo" },
  medium: { color: "#EAB308", label: "Medio" },
  high: { color: "#F97316", label: "Alto" },
  critical: { color: "#EF4444", label: "Crítico" },
};

export default function Approvals() {
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState("pending");

  const load = () => {
    api.get(`/approvals?status_filter=${tab}`).then((r) => setItems(r.data.approvals));
  };

  useEffect(() => { load(); }, [tab]);

  const decide = async (id, decision) => {
    try {
      await api.post(`/approvals/${id}/decision`, { decision });
      toast.success(decision === "approve" ? "Aprobada" : "Rechazada");
      load();
    } catch {
      toast.error("Error al procesar decisión");
    }
  };

  return (
    <div className="p-8 space-y-6" data-testid="approvals-page">
      <div className="border-b border-[var(--border)] pb-5">
        <div className="label-mono mb-2">HUMAN-IN-THE-LOOP</div>
        <h1 className="font-display font-black text-4xl tracking-tight">Bandeja de Aprobaciones</h1>
        <p className="text-sm text-[var(--text-dim)] mt-1">
          Acciones sensibles propuestas por los agentes. Nada se ejecuta sin tu firma.
        </p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="rounded-none bg-[var(--surface)] border border-[var(--border)] p-0 h-auto">
          <TabsTrigger value="pending" className="rounded-none data-[state=active]:bg-[var(--surface-2)] data-[state=active]:text-[var(--accent)] font-mono uppercase text-xs" data-testid="tab-pending">
            <Clock className="w-3 h-3 mr-2" /> Pendientes
          </TabsTrigger>
          <TabsTrigger value="approved" className="rounded-none data-[state=active]:bg-[var(--surface-2)] data-[state=active]:text-[var(--good)] font-mono uppercase text-xs" data-testid="tab-approved">
            <CheckCircle2 className="w-3 h-3 mr-2" /> Aprobadas
          </TabsTrigger>
          <TabsTrigger value="rejected" className="rounded-none data-[state=active]:bg-[var(--surface-2)] data-[state=active]:text-[var(--danger)] font-mono uppercase text-xs" data-testid="tab-rejected">
            <XCircle className="w-3 h-3 mr-2" /> Rechazadas
          </TabsTrigger>
        </TabsList>

        <TabsContent value={tab} className="mt-4">
          {items.length === 0 ? (
            <div className="border border-dashed border-[var(--border)] p-16 text-center" data-testid="empty-approvals">
              <div className="label-mono mb-2">SIN {tab.toUpperCase()}</div>
              <p className="text-sm text-[var(--text-dim)]">
                No hay aprobaciones en esta bandeja. Los agentes crearán una cuando propongan una acción sensible.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {items.map((it) => {
                const risk = RISK_STYLES[it.risk_level] || RISK_STYLES.medium;
                return (
                  <div
                    key={it.id}
                    className="border border-[var(--border)] bg-[var(--surface)] p-5 hover-neon"
                    data-testid={`approval-${it.id}`}
                  >
                    <div className="flex items-start justify-between gap-6">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-2">
                          <Badge
                            className="rounded-none font-mono text-[10px]"
                            style={{ background: risk.color, color: "#0B0E14" }}
                          >
                            <AlertTriangle className="w-3 h-3 mr-1" /> {risk.label}
                          </Badge>
                          <span className="font-mono text-xs text-[var(--text-dim)]">
                            {it.action_type}
                          </span>
                          <span className="font-mono text-xs text-[var(--text-dim)]">·</span>
                          <span className="font-mono text-xs text-[var(--text-dim)] uppercase">
                            {it.agent_id}
                          </span>
                        </div>
                        <div className="font-display font-bold text-lg mb-1">{it.title}</div>
                        <p className="text-sm text-[var(--text-dim)] leading-relaxed">{it.description}</p>
                        <div className="mt-3 label-mono">
                          {new Date(it.created_at).toLocaleString("es-ES")}
                        </div>
                      </div>
                      {tab === "pending" && (
                        <div className="flex flex-col gap-2 shrink-0">
                          <Button
                            onClick={() => decide(it.id, "approve")}
                            className="rounded-none bg-[var(--good)] text-[var(--bg)] hover:bg-[var(--good)]/80"
                            data-testid={`approve-${it.id}`}
                          >
                            <CheckCircle2 className="w-4 h-4 mr-2" /> Aprobar
                          </Button>
                          <Button
                            onClick={() => decide(it.id, "reject")}
                            variant="outline"
                            className="rounded-none border-[var(--danger)] text-[var(--danger)] hover:bg-[var(--danger)]/10"
                            data-testid={`reject-${it.id}`}
                          >
                            <XCircle className="w-4 h-4 mr-2" /> Rechazar
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
